import { useEffect } from 'react';
import { SharedScreenName } from '../config/FlowTypes';
import { StoreStateInternal, useAppStore } from '../store/Store';
import { Environment } from './Environment';

/** Prompt the user to confirm the page unload (back/refresh) if there are unsaved changes.
 *
 * Supported by all browsers except Safari on iOS
 * ([browser compatibility](https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#browser_compatibility)).
 */
export function useConfirmPageUnload(): void {
  const hasUnsavedChanges = useAppStore(unsavedChangesSelector);

  useEffect(
    function unloadListener() {
      function confirmPageUnload(event: BeforeUnloadEvent) {
        // Don't prompt while in dev since we want automatic reload on save.
        if (hasUnsavedChanges && !Environment.DEVELOPMENT) {
          // Note most modern browsers will ignore this and just present a generic message, the important thing
          // is it's set to a non-null value.
          event.returnValue = 'Do you really want to leave? Any progress will be lost.';
        }
      }

      window.addEventListener('beforeunload', confirmPageUnload);

      return () => window.removeEventListener('beforeunload', confirmPageUnload);
    },
    [hasUnsavedChanges]
  );
}

/** While the users is progressing through the assessment there may be data loss. */
function unsavedChangesSelector(state: StoreStateInternal): boolean {
  return (
    state.pageStack.length > 0 &&
    state.pageStack[state.pageStack.length - 1] !== SharedScreenName.DownloadApp
  );
}
